import { useEffect, useState } from 'react';
import { Box, Flex, ScrollArea, Stack, Text, TextInput, UnstyledButton } from '@mantine/core';
import { IconSearch } from '@tabler/icons-react';
import { isSelectableAdvisor, useAdvisors } from 'src/clients/hooks/useAdvisors';
import { useClients } from 'src/clients/hooks/useClients';
import { Modal } from 'src/design-system/Modal';
import { useFlags } from 'src/feature-flags/FlagsHook';
import type { CalendarEvent } from 'src/meetings/models';
import type { MeetingScope } from 'src/meetings/scope';
import { useMeetingAttendees } from 'src/non-financial-data/v2/family/useMeetingAttendees';
import { matchesClientSearch } from 'src/utils/clientSearch';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { formatName, NameFormat } from 'src/utils/nameFormatting';

// A speaker picked from one of the three groups. Clients map to a CLIENT
// participant, team members to a USER, and key contacts to an EXTERNAL one
// (they live on a client's non-financial data, not as platform records).
export type SelectedSpeaker =
  | { kind: 'client'; id: string; name: string }
  | { kind: 'user'; id: string; name: string }
  | { kind: 'keyContact'; name: string; description: string };

type Props = {
  opened: boolean;
  onClose: () => void;
  scope: MeetingScope;
  event: CalendarEvent;
  onSelect: (speaker: SelectedSpeaker) => void;
};

// `identity` is who the row is, independently of which group it came from, so the same person
// found on the event and in the full lists is recognised as one person. `id` only has to be
// unique for React.
type Row = { id: string; identity: string; name: string; detail: string; speaker: SelectedSpeaker };

const identityOf = (speaker: SelectedSpeaker): string =>
  speaker.kind === 'keyContact'
    ? `external:${speaker.name.trim().toLocaleLowerCase()}`
    : `${speaker.kind}:${speaker.id}`;

function SpeakerRow({ row, onPick }: { row: Row; onPick: (speaker: SelectedSpeaker) => void }) {
  return (
    <UnstyledButton
      onClick={() => onPick(row.speaker)}
      px="xs"
      py="xxs"
      style={{ borderRadius: 12 }}
    >
      <Flex align="center" justify="space-between" gap="sm">
        <Text size="textSm" fw={FONT_WEIGHTS.medium} c="gray.10">
          {row.name}
        </Text>
        {row.detail && (
          <Text size="xs" fw={FONT_WEIGHTS.medium} c="gray.6" truncate>
            {row.detail}
          </Text>
        )}
      </Flex>
    </UnstyledButton>
  );
}

// Who the event says is attending, as identities to match against the full lists below.
function attendeeIdentity(
  eventParticipant: CalendarEvent['eventParticipants'][number],
): string | undefined {
  const { client, user, name, participantType } = eventParticipant.participant;
  if (client) return `client:${client.id}`;
  if (user) return `user:${user.id}`;
  if (participantType === 'EXTERNAL' && name?.trim()) {
    return `external:${name.trim().toLocaleLowerCase()}`;
  }
  return undefined;
}

// An external on the event who is nobody's professional connection still has to be offerable,
// so they get a row of their own built from the event.
function unlistedAttendeeRow(
  eventParticipant: CalendarEvent['eventParticipants'][number],
): Row | undefined {
  const { name, description, participantType } = eventParticipant.participant;
  if (participantType !== 'EXTERNAL' || !name?.trim()) return undefined;
  const trimmed = name.trim();
  // A connection can have no type; the backend rejects a blank description with a 422.
  const role = description?.trim() || 'Key contact';
  return {
    id: `attendee:${trimmed}`,
    identity: `external:${trimmed.toLocaleLowerCase()}`,
    name: trimmed,
    detail: role,
    speaker: { kind: 'keyContact', name: trimmed, description: role },
  };
}

export function ChooseSpeakerModal({ opened, onClose, scope, event, onSelect }: Props) {
  const { clients } = useClients();
  const { advisors } = useAdvisors();
  const { offeredConnections: connections } = useMeetingAttendees(scope, event);
  const { flagEnabledClientSearchFullName } = useFlags();
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!opened) setSearch('');
  }, [opened]);

  const pick = (speaker: SelectedSpeaker) => {
    onSelect(speaker);
    onClose();
  };

  const matches = (row: Row) =>
    matchesClientSearch([row.name, row.detail], search, flagEnabledClientSearchFullName);

  // Build the full lists first, then lift the meeting's attendees out of them, so a person on
  // the event and a person in the lists are the same row: one identity, one name, one detail.
  // Search then matches them identically, and nobody can be deduped out of one group while
  // failing to appear in the other.
  const allClientRows: Row[] = clients.map(client => {
    const name = formatName(client.firstName, client.lastName, NameFormat.NATURAL);
    return {
      id: `client:${client.id}`,
      identity: `client:${client.id}`,
      name,
      detail: client.emailAddress ?? '',
      speaker: { kind: 'client' as const, id: client.id, name },
    };
  });

  const allKeyContactRows: Row[] = connections.map(connection => {
    // A connection can have no type; the backend rejects a blank description
    // with a 422, so fall back to "Key contact" (as AddExpectedAttendeesModal does).
    const role = connection.type || 'Key contact';
    const speaker = { kind: 'keyContact' as const, name: connection.name, description: role };
    return {
      id: `contact:${connection.id}`,
      identity: identityOf(speaker),
      name: connection.name,
      detail: role,
      speaker,
    };
  });

  const allTeamRows: Row[] = advisors.filter(isSelectableAdvisor).map(user => {
    const name = formatName(user.firstName, user.lastName, NameFormat.NATURAL);
    return {
      id: `user:${user.id}`,
      identity: `user:${user.id}`,
      name,
      detail: user.emailAddress,
      speaker: { kind: 'user' as const, id: user.id, name },
    };
  });

  const eventParticipants = event.eventParticipants;
  const attendeeIdentities = new Set(
    eventParticipants
      .map(attendeeIdentity)
      .filter((identity): identity is string => identity !== undefined),
  );
  const isAttending = (row: Row) => attendeeIdentities.has(row.identity);

  const listed = [...allClientRows, ...allKeyContactRows, ...allTeamRows];
  const listedIdentities = new Set(listed.map(row => row.identity));
  const unlistedAttendees = eventParticipants
    .map(unlistedAttendeeRow)
    .filter((row): row is Row => row !== undefined && !listedIdentities.has(row.identity));

  const seenAttendees = new Set<string>();
  const attendeeRows: Row[] = [...listed.filter(isAttending), ...unlistedAttendees]
    .filter(row => !seenAttendees.has(row.identity) && (seenAttendees.add(row.identity), true))
    .filter(matches);

  const clientRows = allClientRows.filter(row => !isAttending(row)).filter(matches);
  const keyContactRows = allKeyContactRows.filter(row => !isAttending(row)).filter(matches);
  const teamRows = allTeamRows.filter(row => !isAttending(row)).filter(matches);

  const groups = [
    { key: 'attendees', title: 'On this meeting', rows: attendeeRows },
    { key: 'clients', title: 'Clients', rows: clientRows },
    { key: 'keyContacts', title: 'Key contacts', rows: keyContactRows },
    { key: 'team', title: 'Team', rows: teamRows },
  ].filter(group => group.rows.length > 0);

  return (
    <Modal opened={opened} onClose={onClose} title="Choose a speaker" size={440}>
      <TextInput
        mt="lg"
        value={search}
        onChange={event => setSearch(event.currentTarget.value)}
        placeholder="Search clients, key contacts and team"
        leftSection={<IconSearch size={16} />}
        leftSectionWidth={32}
        styles={{ input: { height: 36, minHeight: 36, borderRadius: 12 } }}
      />

      <ScrollArea.Autosize mah={360} type="never" scrollbars="y" mt="md">
        <Stack gap="lg">
          {groups.map(group => (
            <Box key={group.key}>
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.10" px="xs" mb="xxs">
                {group.title}
              </Text>
              <Stack gap="xxxs">
                {group.rows.map(row => (
                  <SpeakerRow key={row.id} row={row} onPick={pick} />
                ))}
              </Stack>
            </Box>
          ))}

          {groups.length === 0 && (
            <Text size="sm" c="gray.6" px="xs">
              No matching people.
            </Text>
          )}
        </Stack>
      </ScrollArea.Autosize>
    </Modal>
  );
}
